import type { CreativeBriefResponse, ToggleVisibilityFn } from './CreativeBriefs.types';
import {
  Card,
  CardBody,
  CardDescription,
  CardImage,
  CardThumbnail,
  CardTitle,
  HiddenBadge,
  InfoLabel,
  InfoLine,
  InfoLines,
  InfoValue,
  Tag,
  TagRow,
  VisibilityCheckbox,
  VisibilityCheckboxWrapper,
  VisibilityLabel,
  VisibleBadge
} from './styles/briefCard.styles';

type BriefCardProps = {
  brief: CreativeBriefResponse;
  onToggleVisibility: ToggleVisibilityFn;
  updatingVisibility: boolean;
  onClick?: () => void;
};

const BriefCard = ({ brief, onToggleVisibility, updatingVisibility, onClick }: BriefCardProps) => {
  const thumbnailUrl = brief.assets?.[0]?.url;
  const checkboxId = `visibility-${brief.briefId}`;

  const handleCheckboxChange = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!updatingVisibility) {
      onToggleVisibility(brief.briefId, !brief.visibleToCustomer);
    }
  };

  return (
    <Card dimmed={!brief.visibleToCustomer} onClick={onClick}>
      <CardThumbnail>
        {!!thumbnailUrl && <CardImage src={thumbnailUrl} alt={brief.title} loading="lazy" />}
        <VisibilityCheckboxWrapper onClick={handleCheckboxChange}>
          <VisibilityCheckbox
            id={checkboxId}
            type="checkbox"
            checked={brief.visibleToCustomer}
            disabled={updatingVisibility}
            readOnly
          />
          <VisibilityLabel htmlFor={checkboxId}>Visible</VisibilityLabel>
        </VisibilityCheckboxWrapper>
      </CardThumbnail>

      <CardBody>
        <CardTitle>{brief.title}</CardTitle>

        <TagRow>
          <Tag variant="strategy">{brief.strategyName}</Tag>
          <Tag variant={brief.format}>{brief.format}</Tag>
          {brief.visibleToCustomer ? (
            <VisibleBadge>LIVE</VisibleBadge>
          ) : (
            <HiddenBadge>HIDDEN</HiddenBadge>
          )}
        </TagRow>

        <CardDescription>{brief.conceptDescription}</CardDescription>

        <InfoLines>
          <InfoLine>
            <InfoLabel>Product</InfoLabel>
            <InfoValue>{brief.productName}</InfoValue>
          </InfoLine>
          <InfoLine>
            <InfoLabel>Month</InfoLabel>
            <InfoValue>{brief.month}</InfoValue>
          </InfoLine>
          {!!brief.aspectRatio && (
            <InfoLine>
              <InfoLabel>Ratio</InfoLabel>
              <InfoValue>{brief.aspectRatio}</InfoValue>
            </InfoLine>
          )}
        </InfoLines>
      </CardBody>
    </Card>
  );
};

export default BriefCard;
